import { tokens, Divider, Tooltip } from '@hotjar/hotsauce';

import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { ListButton } from './ListButton';
import type { EventEmitter, NavigationProps } from '@hotjar/navigation/src/types';

import { Position } from '@hotjar/navigation/src/events/Position';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';

import { ALL_HIGHLIGHTS_ID } from '@hotjar/navigation/src/constants';

import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';

export interface FeaturesSidebarListProps {
  emitter: EventEmitter;
  isCollapsed: boolean;
  siteId: number;
  currentRouteConfig?: NavigationProps['currentRouteConfig'];
  sidebarNotifications?: NavigationProps['sidebarNotifications'];
  onOpenEngage: () => void;
  hasEngageAccessTeam: boolean;
}

const Container = styled.nav`
  width: 100%;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x0};
`;

export const FeaturesSidebarList = ({
  emitter,
  isCollapsed,
  siteId,
  currentRouteConfig,
  sidebarNotifications,
  onOpenEngage,
  hasEngageAccessTeam,
}: FeaturesSidebarListProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const { routes, buildHrefFromRoute } = useNavigation();
  const isCurrentPath = (path: string): boolean =>
    Boolean(currentRouteConfig?.path?.startsWith(path));
  const hasMicrofrontendPrototype = useFeatureFlag('microfrontend_prototype');

  const urlBuilderConfig: Record<string, number> = { siteId };

  return (
    <Container aria-label="Primary navigation">
      <List>
        <li>
          <ListButton
            text={t('FeatureMenu.overview')}
            icon={isCurrentPath(routes.OVERVIEW.BASE.path) ? 'building_home_alt' : 'building_home'}
            id="overview"
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.OVERVIEW.BASE.path)}
            notificationLabel={sidebarNotifications?.overview}
            path={buildHrefFromRoute(routes.OVERVIEW.BASE.path, urlBuilderConfig)}
            onClick={() => {
              emitter.emit('clickedOverview', { position: Position.toolsToolbar });
            }}
          />
        </li>

        <li>
          <ListButton
            text={t('FeatureMenu.dashboardQuantz')}
            icon={isCurrentPath(routes.DASHBOARD_QUANTZ.BASE.path) ? 'dashboard_alt' : 'dashboard'}
            id="dashboard-quantz"
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.DASHBOARD_QUANTZ.BASE.path)}
            notificationLabel={sidebarNotifications?.dashboardQuantz}
            path={buildHrefFromRoute(routes.DASHBOARD_QUANTZ.BASE.path, urlBuilderConfig)}
            onClick={() => {
              emitter.emit('clickedDashboardQuantz', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.highlights')}
            icon={isCurrentPath(routes.HIGHLIGHTS.LIST.path) ? 'lightbulb_alt' : 'lightbulb'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.HIGHLIGHTS.LIST.path)}
            notificationLabel={sidebarNotifications?.highlights}
            path={buildHrefFromRoute(routes.HIGHLIGHTS.LIST.path, {
              collectionId: ALL_HIGHLIGHTS_ID,
              ...urlBuilderConfig,
            })}
            onClick={() => {
              emitter.emit('clickedHighlights', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <Divider ariaHidden />
        <li>
          <ListButton
            text={t('FeatureMenu.trends')}
            icon={isCurrentPath(routes.TRENDS.BASE.path) ? 'trends_alt' : 'trends'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.TRENDS.BASE.path)}
            notificationLabel={sidebarNotifications?.trends}
            path={buildHrefFromRoute(routes.TRENDS.BASE.path, urlBuilderConfig)}
            onClick={() => {
              emitter.emit('clickedTrends', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.funnels')}
            icon={isCurrentPath(routes.FUNNELS.BASE.path) ? 'funnels_alt' : 'funnels'}
            id="funnels"
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.FUNNELS.BASE.path)}
            notificationLabel={sidebarNotifications?.funnels}
            path={buildHrefFromRoute(routes.FUNNELS.BASE.path, urlBuilderConfig)}
            onClick={() => {
              emitter.emit('clickedFunnels', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.recordings')}
            icon={isCurrentPath(routes.PLAYBACKS.BASE.path) ? 'recordings_alt' : 'recordings'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.PLAYBACKS.BASE.path)}
            notificationLabel={sidebarNotifications?.recordings}
            path={buildHrefFromRoute(routes.PLAYBACKS.BASE.path, urlBuilderConfig)}
            onClick={() => {
              emitter.emit('clickedRecordings', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.heatmaps')}
            icon={isCurrentPath(routes.HEATMAPS.BASE.path) ? 'heatmaps_alt' : 'heatmaps'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.HEATMAPS.BASE.path)}
            notificationLabel={sidebarNotifications?.heatmaps}
            path={buildHrefFromRoute(
              routes.HEATMAPS.LIST_CONTINUOUS_CAPTURE.path,
              urlBuilderConfig,
            )}
            onClick={() => {
              emitter.emit('clickedHeatmaps', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.feedback')}
            icon={isCurrentPath(routes.FEEDBACK.BASE.path) ? 'feedback_alt' : 'feedback'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.FEEDBACK.BASE.path)}
            notificationLabel={sidebarNotifications?.feedback}
            path={buildHrefFromRoute(routes.FEEDBACK.RESPONSES_DEFAULT.path, { siteId })}
            onClick={() => {
              emitter.emit('clickedFeedback', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('FeatureMenu.surveys')}
            icon={isCurrentPath(routes.SURVEYS.BASE.path) ? 'surveys_alt' : 'surveys'}
            isCollapsed={isCollapsed}
            isActive={isCurrentPath(routes.SURVEYS.BASE.path)}
            notificationLabel={sidebarNotifications?.surveys}
            path={buildHrefFromRoute(routes.SURVEYS.RESPONSES_DEFAULT.path, { siteId })}
            onClick={() => {
              emitter.emit('clickedSurveys', { position: Position.toolsToolbar });
            }}
          />
        </li>
        <li>
          <Tooltip
            text={t('FeatureMenu.engage-tooltip')}
            maxWidth={180}
            placement="bottom-start"
            isDisabled={hasEngageAccessTeam || isCollapsed}
          >
            <div>
              <ListButton
                text={t('FeatureMenu.engage')}
                icon="user_multiple"
                isCollapsed={isCollapsed}
                onClick={onOpenEngage}
                isDisabled={!hasEngageAccessTeam}
              />
            </div>
          </Tooltip>
        </li>
        {hasMicrofrontendPrototype && (
          <>
            <Divider ariaHidden />
            <List>
              <li>
                <ListButton
                  text={t('FeatureMenu.recruit')}
                  icon={
                    isCurrentPath('/microfrontend-prototype') ? 'user_avatar_alt' : 'user_avatar'
                  }
                  isCollapsed={isCollapsed}
                  isActive={isCurrentPath('/microfrontend-prototype')}
                  path={buildHrefFromRoute('/microfrontend-prototype')}
                />
              </li>
            </List>
          </>
        )}
      </List>
    </Container>
  );
};
